import { FacilitatorMessageInput } from '@/components/chat/FacilitatorMessageInput';
import { FacilitatorMessageList } from '@/components/chat/FacilitatorMessageList';
import {
  useSendFacilitatorMessage,
  useTeamMessages,
  useTeamsMessageStatus,
} from '@/hooks/useEventMessages';
import { useFacilitatorSocket } from '@/hooks/useFacilitatorSocket';
import { formatCompactRelativeTime } from '@/lib/datetime';
import { cn } from '@/lib/utils';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  toast,
  useApiClient,
} from '@startupquickstart/frontend';
import { useQuery } from '@tanstack/react-query';
import { MessageCircle, Users, Wifi, WifiOff } from 'lucide-react';
import { useMemo, useState } from 'react';

interface Team {
  id: string;
  name: string;
  code: string;
  iconName: string;
  iconColor: string;
  memberCount: number;
}

interface FacilitatorChatPanelProps {
  eventId: string;
}

export function FacilitatorChatPanel({ eventId }: FacilitatorChatPanelProps) {
  const apiClient = useApiClient();
  const [selectedTeamId, setSelectedTeamId] = useState<string | undefined>();

  // Fetch teams for this event
  const { data: teamsData, isLoading: teamsLoading } = useQuery<{
    success: boolean;
    data: Team[];
  }>({
    queryKey: ['EventTeams', eventId],
    queryFn: () => apiClient.get(`/events/${eventId}/teams`),
    enabled: !!eventId,
  });

  const teams = useMemo(() => teamsData?.data || [], [teamsData]);
  const selectedTeam = teams.find((t) => t.id === selectedTeamId);

  // Connect to Socket.IO for real-time updates
  const { isConnected } = useFacilitatorSocket(eventId);

  // Fetch teams' message status (last message time and whether they need response)
  const { data: messageStatusData } = useTeamsMessageStatus(eventId);
  const messageStatusMap = useMemo(() => {
    const map = new Map<string, { lastMessageAt: string; needsResponse: boolean }>();
    messageStatusData?.data?.forEach((item) => {
      map.set(item.teamId, {
        lastMessageAt: item.lastMessageAt,
        needsResponse: item.needsResponse,
      });
    });
    return map;
  }, [messageStatusData]);

  // Sort teams: those needing response first, then by most recent message
  const sortedTeams = useMemo(() => {
    return [...teams].sort((a, b) => {
      const aStatus = messageStatusMap.get(a.id);
      const bStatus = messageStatusMap.get(b.id);
      const aNeedsResponse = aStatus?.needsResponse ?? false;
      const bNeedsResponse = bStatus?.needsResponse ?? false;

      // Teams needing response come first
      if (aNeedsResponse && !bNeedsResponse) return -1;
      if (!aNeedsResponse && bNeedsResponse) return 1;

      // Within each group, sort by most recent message (newest first)
      const aTime = aStatus ? new Date(aStatus.lastMessageAt).getTime() : 0;
      const bTime = bStatus ? new Date(bStatus.lastMessageAt).getTime() : 0;

      // If both have messages, sort by most recent
      if (aTime && bTime) {
        return bTime - aTime; // Most recent first
      }

      // Teams with messages come before teams without
      if (aTime && !bTime) return -1;
      if (!aTime && bTime) return 1;

      // Fallback to alphabetical
      return a.name.localeCompare(b.name);
    });
  }, [teams, messageStatusMap]);

  // Fetch messages for selected team's help channel
  const { data: messagesData, isLoading: messagesLoading } = useTeamMessages(
    eventId,
    selectedTeamId,
    'help',
    { enabled: !!selectedTeamId },
  );

  const messages = messagesData?.data || [];

  // Send message mutation
  const sendMessageMutation = useSendFacilitatorMessage(eventId, selectedTeamId);

  const handleSendMessage = async (content: string) => {
    if (!selectedTeamId) {
      toast.error('Please select a team first');
      return;
    }

    await sendMessageMutation.mutateAsync({ content, channel: 'help' });
  };

  if (teamsLoading) {
    return (
      <Card className="rounded-xl border bg-card shadow-sm h-full flex flex-col gap-0 py-0">
        <CardHeader className="px-6 py-4 !pb-4 border-b shrink-0">
          <Skeleton className="h-5 w-32" />
          <Skeleton className="h-4 w-48" />
        </CardHeader>
        <CardContent className="flex-1 flex !p-0">
          <div className="w-64 border-r p-4 space-y-2">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-12 rounded-lg" />
            ))}
          </div>
          <div className="flex-1 p-4">
            <Skeleton className="h-full rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="rounded-xl border bg-card shadow-sm h-full min-h-[70dvh] flex flex-col gap-0 py-0">
      <CardHeader className="px-4 sm:px-6 py-4 !pb-4 border-b shrink-0">
        {/* Stacks on phones: the title block and team selector fought
            for one row and squeezed the title into a two-line wrap. */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5" />
              Help Channel
              {isConnected ? (
                <span className="flex items-center gap-1 text-xs font-normal text-secondary">
                  <Wifi className="h-3 w-3" />
                  Live
                </span>
              ) : (
                <span className="flex items-center gap-1 text-xs font-normal text-muted-foreground">
                  <WifiOff className="h-3 w-3" />
                </span>
              )}
            </CardTitle>
            <CardDescription>
              View and respond to team help requests
            </CardDescription>
          </div>
          {/* Mobile team selector */}
          <div className="md:hidden">
            <Select value={selectedTeamId} onValueChange={setSelectedTeamId}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Select a team" />
              </SelectTrigger>
              <SelectContent>
                {teams.map((team) => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 min-h-0 flex !p-0 overflow-hidden">
        {/* Team list sidebar - hidden on mobile */}
        <div className="hidden md:flex md:flex-col w-64 border-r bg-muted/20">
          <div className="px-4 py-3 border-b">
            <h3 className="font-mono text-[0.6875rem] font-medium tracking-[0.15em] text-muted-foreground uppercase">
              Teams
            </h3>
          </div>
          <div className="flex-1 overflow-auto">
            {sortedTeams.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No teams registered yet.
              </div>
            ) : (
              <ul className="divide-y">
                {sortedTeams.map((team) => {
                  const status = messageStatusMap.get(team.id);
                  const needsResponse = status?.needsResponse ?? false;
                  const waitTime = status?.lastMessageAt
                    ? formatCompactRelativeTime(status.lastMessageAt)
                    : null;

                  return (
                    <li key={team.id}>
                      <button
                        onClick={() => setSelectedTeamId(team.id)}
                        className={cn(
                          'w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-muted/50',
                          selectedTeamId === team.id && 'bg-muted',
                        )}
                      >
                        <div className="relative">
                          <div
                            className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-medium"
                            style={{
                              backgroundColor: `${team.iconColor}20`,
                              color: team.iconColor,
                            }}
                          >
                            {team.name.charAt(0).toUpperCase()}
                          </div>
                          {needsResponse && (
                            <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full border-2 border-background bg-destructive" />
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm truncate">{team.name}</span>
                            {needsResponse && waitTime && (
                              <span className="rounded bg-destructive/10 px-1.5 py-0.5 text-xs font-medium text-destructive">
                                {waitTime}
                              </span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Users className="h-3 w-3" />
                            {team.memberCount} members
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>

        {/* Chat area */}
        <div className="flex-1 flex flex-col min-w-0">
          {!selectedTeamId ? (
            <div className="flex-1 flex flex-col items-center justify-center p-8 text-center">
              <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center mb-4">
                <MessageCircle className="h-6 w-6 text-muted-foreground" />
              </div>
              <h3 className="font-medium text-foreground mb-1">Select a team</h3>
              <p className="text-sm text-muted-foreground max-w-xs">
                Choose a team from the list to view their help channel messages.
              </p>
            </div>
          ) : (
            <>
              {/* Chat header with team name */}
              <div className="px-4 py-3 border-b bg-muted/30 shrink-0">
                <div className="flex items-center gap-2">
                  <div
                    className="h-6 w-6 rounded-full flex items-center justify-center text-xs font-medium"
                    style={{
                      backgroundColor: `${selectedTeam?.iconColor}20`,
                      color: selectedTeam?.iconColor,
                    }}
                  >
                    {selectedTeam?.name.charAt(0).toUpperCase()}
                  </div>
                  <span className="font-medium text-sm">{selectedTeam?.name}</span>
                  <span className="text-xs text-muted-foreground">
                    ({selectedTeam?.code})
                  </span>
                </div>
              </div>

              {/* Messages */}
              <FacilitatorMessageList
                messages={messages}
                isLoading={messagesLoading}
                teamName={selectedTeam?.name}
              />

              {/* Input */}
              <FacilitatorMessageInput
                onSend={handleSendMessage}
                placeholder={`Message ${selectedTeam?.name || 'team'}...`}
              />
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

